import {
  GRID_TREE_DATA_GROUPING_FIELD,
  type GridColumnsInitialState,
  type GridPinnedColumnFields,
  type GridState,
} from "@mui/x-data-grid-premium";
import { unique } from "@exonn/utils";

export type PersistedGridState = {
  pinnedColumns: GridPinnedColumnFields;
  columns: Pick<
    GridColumnsInitialState,
    "columnVisibilityModel" | "orderedFields" | "dimensions"
  >;
};

type PartialLookupState = Record<
  string,
  Pick<
    GridState["columns"]["lookup"][string],
    "minWidth" | "maxWidth" | "width"
  >
>;

type PartialColumnsState = Pick<
  GridState["columns"],
  "columnVisibilityModel" | "orderedFields"
> & {
  lookup: PartialLookupState;
};

type PartialGridState = Pick<GridState, "pinnedColumns"> & {
  columns: PartialColumnsState;
};

export const mapGridStateToPersistedState = (
  state: PartialGridState,
): PersistedGridState => {
  const nextDimensions = unique(Object.keys(state.columns.lookup)).reduce(
    (acc, field) => {
      const { lookup } = state.columns;
      const { minWidth, maxWidth: unsafeMaxWidth = -1, width } = lookup[field];

      // JSON.stringify converts "Infinity" values to "null",
      // to avoid storing "null" maxWidth in the database "Infinity" is converted -1 beforehand.
      // -1 and Infinity are interchangeable, so there is no harm in it
      const maxWidth = isFinite(unsafeMaxWidth) ? unsafeMaxWidth : -1;

      return {
        ...acc,
        [field]: {
          minWidth,
          width,
          maxWidth,
        },
      };
    },
    {},
  );

  const orderedFields = state.columns?.orderedFields || [];

  const treeData = !!state.columns.lookup[GRID_TREE_DATA_GROUPING_FIELD];

  return {
    columns: {
      columnVisibilityModel: state.columns?.columnVisibilityModel || {},
      orderedFields: unique([
        ...(treeData ? [GRID_TREE_DATA_GROUPING_FIELD] : []), // make sure grouping column is always in the beginning
        ...orderedFields,
      ]),
      dimensions: nextDimensions,
    },
    pinnedColumns: {
      left: unique(state.pinnedColumns.left || []),
      right: unique(state.pinnedColumns.right || []),
    },
  };
};
